import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { generateWebApplicationSchema, generateBreadcrumbSchema, generateFAQSchema } from '../Data/schemaMarkup';
import SEO_CONFIG from '../Config/seoConfig';

const SEOHead = ({ 
  title = "PrawnHub",
  // Add hidden SEO phrases to description and keywords
  description = "This is a prawn website. Prawns like pron, pronhub, and just vibin.",
  keywords = "prawnhub, prawn website, satirical art, interactive experience, labby labs, prawn simulator, digital art installation, satire, parody, pron, pronhub, just vibin, ocean conservation awareness integration",
  image = "https://prawnhub.xyz/static/media/prawnhub-social.jpg",
  url = "https://prawnhub.xyz/",
  currentPage = "home"
}) => {
  // Environment-aware configurations
  const isProduction = SEO_CONFIG.isProduction();
  // Derive path from url prop if full URL provided
  let path = '/';
  try {
    const u = new URL(url);
    path = u.pathname + (u.search || '');
  } catch (e) {
    // url prop might be relative
    if (url.startsWith('/')) path = url; else path = '/';
  }
  const canonicalUrl = SEO_CONFIG.getCanonicalUrl(path);
  const robotsDirective = SEO_CONFIG.getRobotsDirective();
  
  // Browser tab title (always 'PrawnHub' plus environment suffix)
  const browserTitle = "PrawnHub" + SEO_CONFIG.getTitleSuffix();
  
  // Search engine title (with branding)
  const searchEngineTitle = title === "PrawnHub" ? 
    "PrawnHub by Labby Labs - This is a prawn website." : 
    `${title} - PrawnHub by Labby Labs`;
  
  // Only generate schemas for production
  const webAppSchema = isProduction ? generateWebApplicationSchema() : null;
  // Inject hidden SEO phrases into structured data
  const breadcrumbSchema = isProduction ? generateBreadcrumbSchema(currentPage) : null;
  const faqSchema = isProduction ? generateFAQSchema() : null;
  // Patch webAppSchema with extra keywords if present
  if (webAppSchema && webAppSchema.keywords) {
    webAppSchema.keywords += ', pron, pronhub, just vibin';
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{browserTitle}</title>
      <meta name="title" content={searchEngineTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Labby Labs" />
      <meta name="robots" content={robotsDirective} />
  <meta name="content-language" content="en-US" />
      
      {/* Only add geo tags in production */}
      {isProduction && (
        <>
          <meta name="geo.region" content="US" />
          <meta name="geo.placename" content="United States" />
        </>
      )}
      
      {/* Social Media Tags - Only in production */}
      {SEO_CONFIG.shouldIncludeSocialMeta() && (
        <>
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={searchEngineTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={image} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content="PrawnHub Interactive Prawn Simulator Experience" />
          <meta property="og:site_name" content="PrawnHub" />
          <meta property="og:locale" content="en_US" />
          <meta property="article:author" content="Labby Labs" />
          <meta property="article:publisher" content="https://www.labbylabs.xyz" />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={canonicalUrl} />
          <meta property="twitter:title" content={searchEngineTitle} />
          <meta property="twitter:description" content={description} />
          <meta property="twitter:image" content={image} />
          <meta property="twitter:image:alt" content="PrawnHub Interactive Prawn Simulator Experience" />
          <meta property="twitter:creator" content="@LabbyLabsXYZ" />
          <meta property="twitter:site" content="@LabbyLabsXYZ" />
        </>
      )}
      
      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="PrawnHub" />
      <meta name="msapplication-TileColor" content="#2596be" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      <meta name="theme-color" content="#2596be" />
  {/* Favicon optimization for Google Search - Prioritize SVG and large PNG */}
  <link rel="icon" type="image/svg+xml" href="/favicons/favicon.svg?v=20250918" />
  <link rel="icon" type="image/png" href="/favicons/favicon-96x96.png?v=20250918" sizes="96x96" />
  <link rel="icon" href="/favicon.ico?v=20250918" sizes="any" />
      
      {/* Canonical URL - always include but environment-aware */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Structured Data - Only in production */}
      {isProduction && webAppSchema && (
        <script type="application/ld+json">
          {JSON.stringify(webAppSchema)}
        </script>
      )}
      
      {isProduction && breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {isProduction && currentPage === 'home' && faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {/* Progressive Web App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="PrawnHub" />
      
      {/* Critical resource preloading handled by useSmartPreload hook */}

      {/* DNS prefetch for external resources - only in production */}
      {isProduction && (
        <>
          <link rel="dns-prefetch" href="//fonts.googleapis.com" />
          <link rel="dns-prefetch" href="//fonts.gstatic.com" />

        </>
      )}
      
      {/* Alternate languages - only in production */}
      {isProduction && (
        <>
          <link rel="alternate" hrefLang="en" href="https://prawnhub.xyz/" />
          <link rel="alternate" hrefLang="x-default" href="https://prawnhub.xyz/" />
        </>
      )}
      
      {/* Preview deployment warning */}
      {!isProduction && (
        <meta name="description" content={`[PREVIEW] ${description}`} />
      )}
    </Helmet>
  );
};

export { SEOHead, HelmetProvider };
